import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import { applyClasses, applyProps } from '../shared/js';
import Row from './CheckboxRow';
import styles from './checkbox.module.scss';

const Checkbox = ({
  id,
  name,
  style,
  className,
  value,
  children,
  validation,
  checked,
  disabled,
  ...props
}) => {
  const [clicked, setClicked] = useState();
  const formMethods = useFormContext();
  const errors = formMethods?.formState.errors;
  const error = errors && get(errors, name);
  const register = formMethods?.register ? formMethods?.register : () => {};
  const formProps = register(name, { ...validation });

  const handleChange = (e) => {
    setClicked(e.target.checked);
    formProps?.onChange(e);
    props.onChange && props.onChange(e.target.value);
  };

  return (
    <div>
      <div
        className={cn(styles.checkbox, applyClasses(props, styles), {
          [styles.disabled]: disabled,
        })}
      >
        <label
          id={id}
          className={cn(className, styles.label, {
            [styles.hasError]: !!error,
          })}
          style={style}
          onBlur={() => setClicked(false)}
        >
          <input
            className={cn(styles.input, { [styles.clicked]: clicked })}
            type="checkbox"
            name={name}
            value={value}
            defaultChecked={get(formMethods?.getValues(), name) || checked}
            {...formProps}
            {...applyProps(props)}
            onChange={handleChange}
            disabled={disabled}
          />
          <span className={styles.box} />
          <div>{children}</div>
        </label>
      </div>
      {error && (
        <span role="alert" className={styles.errorMessage}>
          {error.message || (
            <FormattedMessage
              id="forms.error"
              description="Error message"
              defaultMessage="Field error"
            />
          )}
        </span>
      )}
    </div>
  );
};

Checkbox.propTypes = {
  /** The ID of the input. Useful to select the input during testing. */
  id: PropTypes.string,
  /** The name of the nested input element */
  name: PropTypes.string,
  /** A callback function that is called when the nested input is checked or unchecked */
  onChange: PropTypes.func,
  /** The content shown to the right of the checkbox  */
  children: PropTypes.any,
  /** A style object to be applied to the parent `label` element */
  style: PropTypes.object,
  /** A class name string to be applied to the parent `label` element */
  className: PropTypes.string,
  /** The value of the checkbox */
  value: PropTypes.string,
  /** The validation rules for the input, see https://react-hook-form.com/get-started#Applyvalidation */
  validation: PropTypes.object,
  /** The form errors object */
  error: PropTypes.object,
  /** The default state of the checkbox */
  checked: PropTypes.bool,
  /** Disable the input */
  disabled: PropTypes.bool,
};

Checkbox.defaultProps = {
  id: undefined,
  name: undefined,
  onChange: () => {},
  children: undefined,
  style: undefined,
  className: undefined,
  value: undefined,
  primary: true,
  secondary: false,
  tertiary: false,
  validation: {},
  error: {},
  checked: false,
  disabled: false,
};

Checkbox.Row = Row;
export default Checkbox;
